import { EntityDefinition } from '@/components/common/CRUDTable/types';

/**
 * Tenant Roadmaps Entity Definition
 * 
 * Defines the structure and configuration for the tenant roadmaps CRUD table.
 * This entity represents downloaded roadmaps that tenants can manage.
 */
export const tenantRoadmapsEntity: EntityDefinition = {
  name: 'Tenant Roadmaps',
  displayName: 'My Roadmaps',
  displayNameSingular: 'Roadmap',
  apiEndpoint: '/tenant/roadmaps',
  routePath: '/tenant/roadmaps',
  idField: 'id',
  nameField: 'name',
  fields: [],
  tableColumns: [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '35%',
      render: (value: any, item: any) => {
        return (
          <div className="flex items-center space-x-2">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
              <p className="text-xs text-gray-500 truncate">{item.description}</p>
            </div>
          </div>
        );
      }
    },
    {
      key: 'projects',
      label: 'Projects',
      sortable: false,
      width: '12%',
      render: (_value: any, item: any) => {
        // Calculate project counts from transformations
        const totalProjects = item.transformation_count || 0;
        const completedProjects = item.completed_transformations || 0;
        const activeProjects = totalProjects - completedProjects;
        
        return (
          <div className="text-center">
            <div className="text-sm font-bold text-gray-900">{totalProjects}</div>
            <div className="text-xs text-gray-500">
              <span className="text-green-600">{completedProjects}✓</span>
              <span className="mx-1">•</span>
              <span className="text-blue-600">{activeProjects}●</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: '12%',
      render: (value: any, _item: any) => {
        const status = String(value || '').toUpperCase();
        const getStatusColor = (status: string) => {
          switch (status) {
            case 'ACTIVE': return 'bg-green-100 text-green-800';
            case 'DRAFT': return 'bg-gray-100 text-gray-800';
            case 'ARCHIVED': return 'bg-yellow-100 text-yellow-800';
            case 'COMPLETED': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
          }
        };
        
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(status)}`}>
            {status}
          </span>
        );
      }
    },
    {
      key: 'functional_area',
      label: 'Area',
      sortable: true,
      width: '15%',
      render: (value: any, _item: any) => {
        return (
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span className="text-sm text-gray-900 truncate">{value || 'N/A'}</span>
          </div>
        );
      }
    },
    {
      key: 'complexity_level',
      label: 'Complexity',
      sortable: true,
      width: '10%',
      render: (value: any, _item: any) => {
        const level = parseInt(value) || 1;
        const getComplexityColor = (level: number) => {
          switch (level) {
            case 1: return 'bg-green-100 text-green-800';
            case 2: return 'bg-blue-100 text-blue-800';
            case 3: return 'bg-yellow-100 text-yellow-800';
            case 4: return 'bg-orange-100 text-orange-800';
            case 5: return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
          }
        };
        
        return (
          <div className="flex items-center space-x-1">
            <div className="flex space-x-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <div
                  key={star}
                  className={`w-1.5 h-1.5 rounded-full ${star <= level ? 'bg-current' : 'bg-gray-300'}`}
                />
              ))}
            </div>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getComplexityColor(level)}`}>
              L{level}
            </span>
          </div>
        );
      }
    },
    {
      key: 'estimated_duration_months',
      label: 'Duration',
      sortable: true,
      width: '10%',
      render: (value: any, _item: any) => {
        const months = parseInt(value) || 0;
        const years = Math.floor(months / 12);
        const remainingMonths = months % 12;
        
        let displayText = '';
        if (years > 0) {
          displayText = `${years}y`;
          if (remainingMonths > 0) {
            displayText += `${remainingMonths}m`;
          }
        } else {
          displayText = `${months}m`;
        }
        
        return (
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-gradient-to-br from-teal-500 to-cyan-600 rounded flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-900">{displayText}</span>
          </div>
        );
      }
    },
    {
      key: 'estimated_cost_range_min',
      label: 'Budget',
      sortable: true,
      width: '12%',
      render: (value: any, item: any) => {
        const minCost = parseFloat(value) || 0;
        const maxCost = parseFloat(item.estimated_cost_range_max) || 0;
        
        const formatCurrency = (amount: number) => {
          if (amount >= 1000000) {
            return `$${(amount / 1000000).toFixed(1)}M`;
          } else if (amount >= 1000) {
            return `$${(amount / 1000).toFixed(0)}K`;
          } else {
            return `$${amount.toFixed(0)}`;
          }
        };
        
        return (
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="text-xs">
              <div className="font-medium text-gray-900">{formatCurrency(minCost)}</div>
              <div className="text-gray-500">-{formatCurrency(maxCost)}</div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: '12%',
      render: (value: any, _item: any) => {
        const date = new Date(value);
        const formattedDate = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: '2-digit'
        });
        
        return (
          <div className="flex items-center space-x-1">
            <div className="w-6 h-6 bg-gradient-to-br from-pink-500 to-rose-600 rounded flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-sm text-gray-900">{formattedDate}</span>
          </div>
        );
      }
    }
  ],
  searchFields: ['name', 'description', 'functional_area', 'transformation_type'],
  defaultSort: { field: 'created_at', direction: 'desc' }
};
